import React, { useEffect, useRef, useState } from 'react';
import { Box, Stack, Typography, debounce } from '@mui/material';
import ReactECharts from 'echarts-for-react';
import LoaderComponent from 'components/Loader';
import HighlightedText from 'components/GlobalSearch/HighlightText';
import CustomDropdown from 'components/Dropdown';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { useTheme } from '@mui/material/styles';
type TrendDataPoint = {
    date: string;
    active: number;
    inactive: number;
};

type DailyPoint = {
    dateString: string;
    day: number;
    activeUsers: number;
    inactiveUsers: number;
    totalUsers: number;
};

type MonthData = {
    month: string;
    monthKey: string;
    activeUsers: number;
    inactiveUsers: number;
    totalUsers: number;
    dailyPoints: DailyPoint[];
};

type ApiResponse = {
    trendData: MonthData[];
    months: string[];
};

type TrendChartProps = {
    isLoading?: boolean;
    title: string | React.ReactNode;
    data: TrendDataPoint[] | ApiResponse;
    hasDropdown?: boolean;
    dropdownValue?: string;
    dropdownOptions?: { label: string; value: string }[];
    onDropdownChange?: (value: string) => void;
    latestDate?: string;
    activeLabel?: string;
    inactiveLabel?: string;
    activeColor?: string;
    inactiveColor?: string;
    containerProps?: any;
};

const TrendChart = ({
    isLoading = false,
    title,
    data = [],
    hasDropdown = false,
    dropdownValue,
    dropdownOptions = [],
    onDropdownChange,
    latestDate,
    activeLabel = 'Active Licenses',
    inactiveLabel = 'Inactive Licenses',
    activeColor,
    inactiveColor,
    containerProps
}: TrendChartProps) => {
    const theme = useTheme();
    const colors = theme.palette;
    const resolvedActiveColor = activeColor || colors.boxColor?.blue?.color;
    const resolvedInactiveColor = inactiveColor || colors.boxColor?.red?.bgColor;
    const chartRef = useRef<any>(null);
    const [windowSize, setWindowSize] = useState(window.innerWidth);

    useEffect(() => {
        const handleResize = debounce(() => {
            if (chartRef.current) {
                chartRef.current?.getEchartsInstance().resize();
            }
            setWindowSize(window.innerWidth);
        }, 200);

        handleResize();
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Check if data is API response format or legacy format
    const isApiResponse = (data: any): data is ApiResponse => {
        return data && 'trendData' in data && Array.isArray(data.trendData);
    };

    let xAxisLabels: string[] = [];
    let activeData: number[] = [];
    let inactiveData: number[] = [];
    let monthBreakpoints: { index: number; monthLabel: string }[] = [];
    let allDailyPoints: DailyPoint[] = [];

    if (isApiResponse(data)) {
        // Handle API response format
        const rawData = data.trendData || [];

        // Flatten all daily points from all months and track month breakpoints
        rawData.forEach((monthData: MonthData) => {
            if (monthData.dailyPoints && monthData.dailyPoints.length > 0) {
                // Mark the start of each month
                monthBreakpoints.push({
                    index: allDailyPoints.length,
                    monthLabel: monthData.month // e.g., "Dec 25"
                });

                allDailyPoints = [...allDailyPoints, ...monthData.dailyPoints];
            }
        });

        // Sort by date to ensure chronological order
        allDailyPoints.sort((a: DailyPoint, b: DailyPoint) => 
            new Date(a.dateString).getTime() - new Date(b.dateString).getTime()
        );

        // Extract data for chart
        allDailyPoints.forEach((point: DailyPoint) => {
            // Format date for display (e.g., "04 Dec")
            const date = new Date(point.dateString);
            const displayDate = `${String(date.getDate()).padStart(2, '0')} ${date.toLocaleString('en-US', { month: 'short' })}`;

            xAxisLabels.push(displayDate);
            activeData.push(point.activeUsers || 0);
            inactiveData.push(point.inactiveUsers || 0);
        });
    } else {
        // Handle legacy format (array of TrendDataPoint)
        const legacyData = data as unknown as TrendDataPoint[];
        for (const item of legacyData) {
            xAxisLabels.push(item.date);
            activeData.push(item.active);
            inactiveData.push(item.inactive);
        }
    }

    // Get the last data point index for markLine and labels
    const lastDataIndex = xAxisLabels.length > 0 ? xAxisLabels.length - 1 : -1;

    const chartOption: any = {
    backgroundColor: colors.widget?.background?.chart,
    tooltip: {
        trigger: 'axis' as const,
        axisPointer: {
            show: false
        },
        backgroundColor: colors.widget?.chart?.tooltipBg,
        borderColor: colors.widget?.chart?.legendInactiveUpDownIconColor,
        borderWidth: 1,
        textStyle: {
            color: colors.widget?.chart?.tooltipText,
            fontSize: 12
        },
        formatter: function (params: any) {
            const dataIndex = params[0].dataIndex;

            // Format the date properly for tooltip
            let formattedDate = xAxisLabels[dataIndex];
            if (isApiResponse(data) && allDailyPoints[dataIndex]) {
                const rawDate = allDailyPoints[dataIndex].dateString;
                const parsedDate = new Date(rawDate);
                if (!Number.isNaN(parsedDate.getTime())) {
                    formattedDate = new Intl.DateTimeFormat('en-GB', {
                        day: 'numeric',
                        month: 'long',
                        year: 'numeric'
                    }).format(parsedDate);
                }
            }

            let tooltipContent = `<div style="margin-bottom: 5px; font-weight: bold;">${formattedDate}</div>`;

            for (const param of params) {
                const color = param.color;
                const seriesName = param.seriesName;
                const value = param.value.toLocaleString();
                tooltipContent += `
                    <div style="display: flex; align-items: center; margin-bottom: 3px;">
                      <span style="display: inline-block; width: 10px; height: 10px; background-color: ${color}; margin-right: 8px; border-radius: 2px;"></span>
                      <span style="margin-right: 8px;">${seriesName}:</span>
                      <span style="font-weight: bold;">${value}</span>
                    </div>
                `;
            }

            return tooltipContent;
        }
    },
    legend: {
        data: [activeLabel, inactiveLabel],
        textStyle: {
            color: colors.widget?.chart?.legendTextColor,
            fontSize: 11
        },
        itemWidth: 14,
        itemHeight: 14,
        itemGap: 15,
        icon: 'roundRect',
        bottom: '0',
        left: 'left'
    },
    grid: {
        left: '3%',
        right: '4%',
        bottom: '15%',
        top: '10%',
        containLabel: true
    },
    xAxis: {
        type: 'category' as const,
        boundaryGap: false,
        data: xAxisLabels,
        axisLabel: {
            color: colors.widget?.chart?.legendTextColor,
            fontSize: 11,
            interval: 0,
            rotate: 45,
            formatter: function (value: string, index: number) {
                // Show month labels only at breakpoints
                if (isApiResponse(data)) {
                    const monthBreakpoint = monthBreakpoints.find(bp => bp.index === index);
                    return monthBreakpoint ? monthBreakpoint.monthLabel : '';
                }
                // For legacy data, show all labels
                return value;
            }
        },
        axisLine: {
            show: false
        },
        axisTick: {
            show: false
        },
        splitLine: {
            show: false
        },
        splitArea: {
            show: false
        }
    },
    yAxis: {
        type: 'value' as const,
        axisLabel: {
            color: colors.widget?.chart?.legendTextColor,
            fontSize: 11,
            formatter: function (value: number) {
                if (value >= 1000000) {
                    return (value / 1000000).toFixed(1) + 'M';
                } else if (value >= 1000) {
                    return (value / 1000).toFixed(1) + 'k';
                }
                return value.toString();
            }
        },
        axisLine: {
            show: false
        },
        axisTick: {
            show: false
        },
        splitLine: {
            show: false
        },
        splitArea: {
            show: false
        }
    },
    series: [
        {
            name: activeLabel,
            type: 'line',
            data: activeData,
            smooth: false,
            symbol: 'circle',
            symbolSize: 6,
            lineStyle: {
                color: resolvedActiveColor,
                width: 3
            },
            itemStyle: {
                color: resolvedActiveColor
            },
            label: {
                show: false
            }
        },
        {
            name: inactiveLabel,
            type: 'line',
            data: inactiveData,
            smooth: false,
            symbol: 'circle',
            symbolSize: 6,
            lineStyle: {
                color: resolvedInactiveColor,
                width: 3
            },
            itemStyle: {
                color: resolvedInactiveColor
            },
            label: {
                show: false
            }
        }
    ]
};

    const defaultOption = {
        backgroundColor: colors.widget?.background?.chart,
        graphic: [
            {
                type: 'text',
                left: 'center',
                top: 'center',
                style: {
                    text: 'No data',
                    fill: colors.widget?.chart?.legendTextColor,
                    fontSize: 16
                }
            }
        ]
    };

    return (
        <Box 
            display="flex" 
            position="relative" 
            sx={{
                width: '100%',
                ...containerProps?.sx
            }}
        >
            <Stack
                minHeight="100px"
                p={2}
                bgcolor={colors.widget?.background?.card}
                fontFamily="Montserrat"
                borderRadius="5px"
                sx={{
                    flexDirection: 'column',
                    width: '100%',
                    flexGrow: 1
                }}
            >
                <Stack
                    direction="row"
                    gap={2}
                    justifyContent="space-between"
                    alignItems="center"
                    minHeight="38px"
                    mb={1}
                    sx={{ flexShrink: 0 }}
                >
                    <Typography variant="widgetTitle" sx={{ color: colors.widget?.text?.title }}>
                        {typeof title === 'string' ? <HighlightedText text={title} /> : title}
                    </Typography>
                    <Stack direction="row" gap={1} alignItems="center">
                        {hasDropdown && dropdownValue && dropdownOptions.length > 0 && (
                            <CustomDropdown
                                value={dropdownValue}
                                options={dropdownOptions}
                                handleChange={(event: any) => {
                                    if (onDropdownChange) {
                                        onDropdownChange(event.target.value);
                                    }
                                }}
								dropdownIcon={KeyboardArrowDownIcon}
                                selectedTextColor={colors.DropdownV2?.dropdownIconDefault}
                                textColor={colors.DropdownV2?.menuItemText}
                                iconColor={colors.DropdownV2?.dropdownIconDefault}
                                bgColor={colors.DropdownV2?.menuItemBackground}
                                borderColor="transparent"
                                hoverBgColor={colors.DropdownV2?.menuItemHover}
                                hoverTextColor={colors.DropdownV2?.menuItemHoverText}
                                selectedBgColor={colors.DropdownV2?.menuItemSelectedBg}
                                boxProps={{
                                sx: {
                                    backgroundColor: 'transparent !important',
                                    width: 'fit-content',
                                    minWidth: 'unset',
                                    '& .MuiOutlinedInput-root': {
                                        backgroundColor: 'transparent !important',
                                        width: 'fit-content',
                                        minWidth: 'unset'
                                    },
                                    '& .MuiSelect-select': {
                                        fontFamily: 'Montserrat',
                                        fontSize: '14px',
                                        fontWeight: 500,
                                        padding: '0 24px 0 8px !important',
                                        minHeight: '28px !important',
                                        width: 'auto',
                                        minWidth: 'unset',
                                        display: 'flex',
                                        alignItems: 'center'
                                    },
                                    '& .MuiOutlinedInput-notchedOutline': {
                                        border: 'none !important'
                                    },
                                    '& .MuiSelect-icon': {
                                        color: `${colors.DropdownV2?.dropdownIconDefault} !important`,
                                        right: 0
                                    }
                                }
                            }}
                            />
                        )}
                    </Stack>
                </Stack>

                <Box 
                    flexGrow={1} 
                    minHeight="300px" 
                    position="relative"
                    sx={{ 
                        overflow: 'hidden',
                        width: '100%'
                    }}
                >
                    {isLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                            <LoaderComponent />
                        </Box>
                    ) : (
                        <ReactECharts
                            key={windowSize}
                            ref={chartRef}
                            option={xAxisLabels.length > 0 ? chartOption : defaultOption}
                            style={{ width: '100%', height: '100%', minHeight: '300px' }}
                            opts={{ renderer: 'canvas', width: 'auto', height: 'auto' }}
                            notMerge={true}
                            lazyUpdate={true}
                        />
                    )}
                </Box>
            </Stack>
        </Box>
    );
};

export default TrendChart;
